/**
 * @file StrategyDetail.jsx
 * @description Main application interface with role-specific features
 * 
 * @section Responsibilities
 * - Manage role-based views
 * - Handle data aggregation
 * - Control real-time updates
 * - Manage user preferences
 * - Handle document management
 * 
 * @section Dependencies
 * - Role Management: Access control
 * - Data Services: Information handling
 * - State Management: Global state
 * - Document Services: File handling
 * 
 * @section Integration Points
 * - Data API: Information retrieval
 * - Role System: Access control
 * - Notification System: Real-time updates
 * - Document API: File management
 * 
 * @section State Management
 * - dashboardData: Aggregated information
 * - loadingState: Data fetching
 * - errorState: Error handling
 * - documents: Document list
 * - uploadState: File upload status
 * 
 * @section Events
 * - onDataUpdate: Data changes
 * - onError: Error handling
 * - onPreferenceChange: Settings updates
 * - onDocumentUpload: File upload
 * - onDocumentDelete: File deletion
 * 
 * @section Props
 * @prop {String} userRole - Current user role
 * @prop {Object} permissions - User permissions
 */

import React, { useState, useCallback, useRef } from "react";
import { useParams } from "react-router-dom";
import SkinPage from "../../SkinPage";
import Widget from "../../widgets/Widget";
import Request from "../../../../utils/fetchUtils";
import TradingDash from "../../widgets/trading/TradingDash";
import OrdersHistory from "../../widgets/trading/OrdersHistory";
import Positions from "../../widgets/trading/Positions";
import PendingRequests from "../../widgets/trading/PendingRequests";
import {
  LineChart,
  Line,
  Tooltip,
  ReferenceLine,
  YAxis,
  XAxis,
} from "recharts";
import { useEffect } from "react";
import Loader from "../../widgets/Loader";
import { TODAY, toISOString } from "../../../../utils/dateUtils";
import Swal from "sweetalert2";
import FilterSelect from "../../../../components/dashboard/FilterSelect";
import { getFromSession } from "../../../../utils/sessionUtils";
import WarningIcon from "@mui/icons-material/Warning";
import EditIcon from "@mui/icons-material/Edit";
import HistoryIcon from "@mui/icons-material/History";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import DescriptionIcon from "@mui/icons-material/Description";
import DgModal from "../../../../components/DgModal";
import RiskHistoryModal from "../../../../components/RiskHistoryModal";
import DgButton from "../../../../components/DgButton";
import TextInput from "../../../../components/forms/TextInput";

function StrategyDetail() {
  const { id } = useParams();
  const sessionRole = getFromSession("role");
  const isCustomer = sessionRole === "customer";
  const canManageDocuments = ["fundManager", "admin"].includes(sessionRole);
  
  // File input ref
  const fileInputRef = useRef(null);
  
  // Original states
  const [strategy, setStrategy] = useState({});
  const [historical, setHistorical] = useState({});
  const [orders, setOrders] = useState([]);
  const [closingPosition, setClosingPosition] = useState(null);
  const [loading, setLoading] = useState(false);
  const [editRiskModal, setEditRiskModal] = useState(false);
  const [riskScoreInput, setRiskScoreInput] = useState("");
  const [showRiskHistory, setShowRiskHistory] = useState(false);

  // Document states
  const [documents, setDocuments] = useState([]);
  const [documentsModal, setDocumentsModal] = useState(false);
  const [uploadingFile, setUploadingFile] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const handleFetchData = async (queryFilters = "") => {
    const sess = JSON.parse(localStorage.session);
    const userId = sess?.userId;

    if (typeof userId === "string") {
      setLoading(true);
      const res = await Request(`/trading/strategy/${id}?q=&${queryFilters}`);
      if (res.ok) {
        setLoading(false);
        setStrategy(res.data);
        setHistorical(res.data?.historical);
      }

      const resOrders = await Request(`/trading/strategy/${id}/orders`);
      if (resOrders.ok) {
        setOrders(resOrders.data);
      }
    }
  };

  const handleFileSelect = () => {
    fileInputRef.current?.click();
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    // Validate file size (e.g., 10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      Swal.fire("Error", "File size must be less than 10MB", "error");
      return;
    }

    // Validate file type
    const allowedTypes = ['application/pdf'];
    if (!allowedTypes.includes(file.type)) {
      Swal.fire("Error", "Only PDF files are allowed", "error");
      return;
    }

    setUploadingFile(true);
    setUploadProgress(0);

    try {
      // Step 1: Get upload URL
      const uploadUrlRes = await Request(
        `/trading/strategy/${id}/documents/upload-url`,
        {
          fileName: file.name,
        },
        "POST",
      );
      
      
      if (!uploadUrlRes || !uploadUrlRes.ok) {
        throw new Error(uploadUrlRes?.error || uploadUrlRes?.message || "Failed to get upload URL");
      }

      if (!uploadUrlRes.data || !uploadUrlRes.data.uploadUrl || !uploadUrlRes.data.documentInfo) {
        throw new Error("Invalid response structure from server");
      }

      const { uploadUrl, documentInfo } = uploadUrlRes.data;

      // Step 2: Upload file to S3 with progress tracking

     await new Promise((resolve, reject) => {
        const xhr = new XMLHttpRequest();
        
        // Progress tracking
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const progress = Math.round((event.loaded / event.total) * 100);
            setUploadProgress(progress);
          }
        });
        
        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve({ ok: true, status: xhr.status });
          } else {
            reject(new Error(`Upload failed: ${xhr.status} ${xhr.statusText}`));
          }
        });
        
        xhr.addEventListener('error', () => {
          console.error("S3 Upload network error");
          reject(new Error('Network error during upload'));
        });
        
        xhr.open('PUT', uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });

     

      // Step 3: Confirm upload
      const confirmRes = await Request(
        `/trading/strategy/${id}/documents/confirm-upload`,
        {
          strategyId: id, 
          documentInfo: documentInfo
        },
         "POST"
      );

      if (!confirmRes || !confirmRes.ok) {
        throw new Error(confirmRes?.error || confirmRes?.message || "Failed to confirm upload");
      }

      // Refresh documents list
      await fetchDocuments();
      
      Swal.fire("Success", "Document uploaded successfully", "success");
      
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }

    } catch (error) {
      console.error("Upload error:", error);
      
      let errorMessage = "Failed to upload document";
      if (error.message) {
        errorMessage = error.message;
      }
      
      Swal.fire("Error", errorMessage, "error");
    } finally {
      setUploadingFile(false);
      setUploadProgress(0);
    }
  };

 const fetchDocuments = async () => {
    try {
      const res = await Request(
        `/trading/strategy/${id}/documents`,
        'GET'
      );
      
      if (res.ok) {
        const documentsArray = res.data?.documents || [];
        setDocuments(Array.isArray(documentsArray) ? documentsArray : []);
      } else {
        console.error("Error fetching documents:", res.error);
        setDocuments([]);
      }
    } catch (error) {
      console.error("Error fetching documents:", error);
      setDocuments([]);
    }
  }; 


  const handleDocumentDownload = async (documentId, fileName) => {
    try {
      const res = await Request(
        `/trading/strategy/${id}/documents/${documentId}/download`,
      );
    
      if (res.ok) {
        const { downloadUrl } = res.data;
        
        try {
          const response = await fetch(downloadUrl);
          const blob = await response.blob();
          
          const blobUrl = window.URL.createObjectURL(blob);
      
          const link = document.createElement('a');
          link.href = blobUrl;
          link.download = fileName;
          link.style.display = 'none';
          
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          window.URL.revokeObjectURL(blobUrl);
          
        } catch (fetchError) {
          console.warn('Fetch method failed, trying alternative method:', fetchError);
          
          const link = document.createElement('a');
          link.href = downloadUrl;
          link.download = fileName;
          link.target = '_blank';
          link.rel = 'noopener noreferrer';
          
          link.setAttribute('download', fileName);
          
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        }
        
      } else {
        Swal.fire("Error", "Failed to generate download URL", "error");
      }
    } catch (error) {
      console.error("Download error:", error);
      Swal.fire("Error", "Failed to download document", "error");
    }
  };


  const handleDocumentDelete = async (documentId, fileName) => {
    const result = await Swal.fire({
      title: 'Delete Document',
      text: `Are you sure you want to delete "${fileName}"?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    });

    if (result.isConfirmed) {
      try {
        const res = await Request(
          `/trading/strategy/${id}/documents/${documentId}`,
          {},
          'DELETE'
        );

        if (res.ok) {
          await fetchDocuments();
          Swal.fire("Deleted", "Document has been deleted", "success");
        } else {
          const errorData = await res.text();
          console.error('Error response:', errorData);
          Swal.fire("Error", "Failed to delete document", "error");
        }
      } catch (error) {
        console.error("Delete error:", error);
        Swal.fire("Error", "Failed to delete document", "error");
      }
    }
  };

  const handleDocumentsModal = (isOpen) => {
    setDocumentsModal(isOpen);
    if (isOpen) {
      fetchDocuments();
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const fetch = useCallback(() => {
    handleFetchData();
  }, []);

  const mapHistoricalToLineChart = (strategy) => {
    const data =
      strategy?.historical?.map((entry) => ({
        name: entry?.date,
        amount: entry?.totalEquity,
      })) || [];

    const minEquity = Math.min(...data.map((d) => d.amount));
    const maxEquity = Math.max(...data.map((d) => d.amount));

    return { data, minEquity, maxEquity };
  };

  const getStrategyRiskScore = async () => {
    setLoading(true);
    const res = await Request(`/trading/risk/strategy/${id}`);
    setLoading(false);
    if (res.ok) {
      setStrategy({
        ...strategy,
        riskScore: res.data.riskAnalysis.risk_number,
        lastRiskCalculated: toISOString(TODAY),
      });
      Swal.fire("Risk calculated", "", "success");
    }
  };

  const handleEditRiskModal = (isOpen) => {
    setEditRiskModal(isOpen);
    if (isOpen) {
      setRiskScoreInput(strategy.riskScore || "");
    }
  };

  const handleEditRisk = () => {
    handleEditRiskModal(true);
  };

  const handleShowRiskHistory = () => {
    setShowRiskHistory(true);
  };

  const updateRiskScore = async () => {
    const riskEstimate = parseFloat(riskScoreInput);
    
    // Validation
    if (!riskScoreInput || riskEstimate < 0 || riskEstimate > 100) {
      Swal.fire(
        "Invalid Input!",
        "Please enter a valid value between 0 and 100",
        "warning"
      );
      return;
    }

    setLoading(true);
    try {
      const res = await Request(
        `/trading/strategy/risk`, 
        {
          strategyId: id,
          riskEstimate: riskEstimate
        },
        'PUT'
      );

      setLoading(false);
      
      if (res.ok) {
        setStrategy({
          ...strategy,
          riskScore: riskEstimate,
          lastRiskCalculated: toISOString(TODAY),
          riskHistory: res.data?.riskHistory || strategy.riskHistory,
          lastUpdated: res.data?.lastUpdated || {
            userId: sessionRole,
            newRisk: riskEstimate,
            timestamp: toISOString(TODAY)
          }
        });
        handleEditRiskModal(false);
        handleFetchData()
        Swal.fire('Updated!', 'Risk score has been updated successfully', 'success');
      } else {
        Swal.fire('Error', 'Could not update risk score', 'error');
      }
    } catch (error) {
      setLoading(false);
      Swal.fire('Error', 'An error occurred while updating risk score', 'error');
    }
  };

  const handleClosingPosition = (pos) => {
    setClosingPosition(pos);
  };

  useEffect(() => {
    fetch();
  }, []);

  return (
    <SkinPage>
      <Widget name={"strategy-detail"}>
        <div className="heading">
          <h1 className="title">{strategy.name}</h1>
          {isCustomer && (
            <>
              <p style={{ color: "#95b2c5" }}>
                <WarningIcon />
                {"  "}The strategies and views presented are solely those of the
                fund manager and do not necessarily reflect the positions of
                your personal portfolio client. All investment strategies and
                approaches are implemented independently by the fund manager and
                are based on their professional judgment and expertise. For
                further clarification, please consult with your advisor or refer
                to the official documentation provided
              </p>
            </>
          )}
        </div>
        <div className="data-info">
          <div className="features">
            <div className="main-feature">
              Total clients: {strategy.customers?.length || 0}
            </div>
            <div className="main-feature">
              Orders replicated: {strategy.orders?.length || 0}
            </div>
            <div className="main-feature">
              Performance:{" "}
              {typeof strategy.timeWeightedPerformance === "number"
                ? `${strategy.timeWeightedPerformance.toFixed(2)}%`
                : "-"}
            </div>
            <div className="main-feature">
              <span>Risk score:</span>
              <br />
              <div style={{ 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center", 
                gap: "8px", 
                width: "100%" 
              }}>
                <span style={{ textAlign: "center" }}>
                  {strategy.riskScore || "-"}
                </span>
                {!isCustomer && (
                  <div style={{ display: "flex", gap: "4px" }}>
                    <EditIcon 
                      style={{ 
                        fontSize: "18px", 
                        cursor: "pointer", 
                        color: "#3357FF",
                        opacity: 0.7
                      }}
                      onClick={handleEditRisk}
                      title="Editar risk score"
                    />
                    {strategy.riskHistory && strategy.riskHistory.length > 0 && (
                      <HistoryIcon 
                        style={{ 
                          fontSize: "18px", 
                          cursor: "pointer", 
                          color: "#666",
                          opacity: 0.7
                        }}
                        onClick={handleShowRiskHistory}
                        title="Ver historial de cambios"
                      />
                    )}
                  </div>
                )}
              </div>
            </div>
          
            <div className="main-feature">
              <span>Documents:</span>
              <br />
              <div style={{ 
                display: "flex", 
                alignItems: "center", 
                justifyContent: "center", 
                gap: "8px", 
                width: "100%" 
              }}>
                <AttachFileIcon 
                  style={{ 
                    fontSize: "18px", 
                    cursor: "pointer", 
                    color: "#3357FF",
                    opacity: 0.7
                  }}
                  onClick={() => handleDocumentsModal(true)}
                  title="Ver documentos"
                />
                <span style={{ fontSize: "12px", color: "#666" }}>
                  View Documents
                </span>
              </div>
            </div>
          </div>

          <FilterSelect onFilter={handleFetchData} />

          <div className="performance-chart">
            {(() => {
              const { data, minEquity, maxEquity } =
                mapHistoricalToLineChart(strategy);
              return (
                <LineChart width={800} height={200} data={data}>
                  <Line
                    type="monotone"
                    dataKey="amount"
                    stroke="#3357FF"
                    strokeWidth={2}
                  />
                  <Tooltip />
                  <XAxis dataKey="name" height={60} />
                  <YAxis type="number" domain={[minEquity, maxEquity]} />
                  <ReferenceLine
                    y={100000}
                    stroke="white"
                    strokeDasharray="3 3"
                  />
                </LineChart>
              );
            })()}
          </div>
        </div>
      </Widget>
      
      {!isCustomer && <PendingRequests strategy={strategy} fetch={fetch} />}
      {!isCustomer && (
        <TradingDash
          strategy={strategy}
          closingPosition={closingPosition}
          handleFetchData={handleFetchData}
        />
      )}
      <OrdersHistory orders={orders} strategyId={id} />
      <Positions
        strategyId={id}
        getClosingPosition={handleClosingPosition}
        positions={strategy.positions}
      />
      
      {/* Edit Risk Score Modal */}
      <DgModal
        modalState={editRiskModal}
        handleModalState={handleEditRiskModal}
      >
        <h1 className="title">Edit Risk Score</h1>
        <div className="strategy-content">
          <div className="form-field">
            <TextInput
              customInput={{
                label: "Risk Score",
                name: "riskScore",
                type: "number",
                tooltipBool: true,
                tooltipText: "Enter a value between 0 and 100",
                value: riskScoreInput,
                action: (e) => {
                  setRiskScoreInput(e.target.value);
                },
                min: 0,
                max: 100,
                step: 0.1
              }}
            />
          </div>
        </div>
        <DgButton
          category="primary"
          size="large"
          text="Update Risk Score"
          disabled={loading}
          action={updateRiskScore}
        />
      </DgModal>

      {/* Documents Modal */}
      <DgModal
        modalState={documentsModal}
        handleModalState={handleDocumentsModal}
        size="large"
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "20px" }}>
          <h1 className="title">Strategy Documents</h1>
          {canManageDocuments && (
            <DgButton
              category="primary"
              size="medium"
              text="Upload Document"
              icon={<UploadFileIcon />}
              action={handleFileSelect}
              disabled={uploadingFile}
            />
          )}
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          style={{ display: 'none' }}
          onChange={handleFileUpload}
          accept=".pdf"
        />

        {/* Upload progress */}
        {uploadingFile && (
          <div style={{ marginBottom: "20px" }}>
            <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "5px" }}>
              <span>Uploading...</span>
              <span>{uploadProgress}%</span>
            </div>
            <div style={{ 
              width: "100%", 
              backgroundColor: "#f0f0f0", 
              borderRadius: "10px",
              height: "10px" 
            }}>
              <div style={{ 
                width: `${uploadProgress}%`, 
                backgroundColor: "#3357FF", 
                borderRadius: "10px",
                height: "100%",
                transition: "width 0.3s ease"
              }}></div>
            </div>
          </div>
        )}

        {/* Documents list -*/}
        <div className="documents-list" style={{ maxHeight: "400px", overflowY: "auto" }}>
          {documents.length === 0 ? (
            <div style={{ 
              textAlign: "center", 
              padding: "40px", 
              color: "#666",
              border: "2px dashed #ddd",
              borderRadius: "8px"
            }}>
              <DescriptionIcon style={{ fontSize: "48px", marginBottom: "10px", opacity: 0.5 }} />
              <p>No documents uploaded yet</p>
              {canManageDocuments && (
                <p style={{ fontSize: "14px" }}>Click "Upload Document" to add files</p>
              )}
            </div>
          ) : (
            <div style={{ display: "grid", gap: "10px" }}>
              {documents.map((doc) => (
                <div 
                  key={doc.documentId} 
                  style={{ 
                    display: "flex", 
                    alignItems: "center", 
                    justifyContent: "space-between",
                    padding: "15px",
                    border: "1px solid #e0e0e0",
                    borderRadius: "8px",
                    backgroundColor: "#f9f9f9"
                  }}
                >
                  <div style={{ display: "flex", alignItems: "center", gap: "10px", flex: 1 }}>
                    <span style={{ fontSize: "24px" }}>
                      <DescriptionIcon style={{ color: "#3357FF", opacity: 0.7 }} />
                    </span>
                    <div>
                      <div style={{ fontWeight: "500", marginBottom: "2px" }}>
                        {doc.fileName}
                      </div>
                      <div style={{ fontSize: "12px", color: "#666" }}>
                        {doc.size ? formatFileSize(doc.size) : 'Size unknown'} • Uploaded {new Date(doc.uploadedAt).toLocaleDateString()}
                        {doc.uploadedBy && ` by ${doc.uploadedBy}`}
                      </div>
                      {doc.description && (
                        <div style={{ fontSize: "11px", color: "#888", marginTop: "2px" }}>
                          {doc.description}
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: "8px" }}>
                    <DownloadIcon
                      style={{ 
                        fontSize: "20px", 
                        cursor: "pointer", 
                        color: "#3357FF",
                        opacity: 0.7
                      }}
                      onClick={() => handleDocumentDownload(doc.documentId, doc.fileName)}
                      title="Download document"
                    />
                    {canManageDocuments && (
                      <DeleteIcon
                        style={{ 
                          fontSize: "20px", 
                          cursor: "pointer", 
                          color: "#d33",
                          opacity: 0.7
                        }}
                        onClick={() => handleDocumentDelete(doc.documentId, doc.fileName)}
                        title="Delete document"
                      />
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </DgModal>

      {/* Risk History Modal */}
      <RiskHistoryModal
        isOpen={showRiskHistory}
        onClose={() => setShowRiskHistory(false)}
        riskHistory={strategy.riskHistory || []}
        strategyName={strategy.name}
      />
      
      {loading && <Loader />}
    </SkinPage>
  );
}

export default StrategyDetail;